import { useCallback, useEffect } from "react";

import * as Sentry from "@sentry/react";
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import zoomSdk, { RunningContextResponse } from "@zoom/appssdk";

import useApps from "@app/common/hooks/useApps";
import { configureZoomSdk } from "@app/embedded/utils";
import logger from "@app/utils/logger";
import Tracker from "@app/utils/tracking/Tracker";

type RunningContext = RunningContextResponse["context"] | "waiting" | "unknown";

type UseZoomRunningContextResponse = UseQueryResult<RunningContext> & {
  runningContext: RunningContext;
};

// Zoom once had a bug where getRunningContext stopped working
// and we used the following algo to infer the running context
// this is here in the case the bug happens again, but we might
// be able to get rid of this extra logic someday
const getInferredRunningContext = async (): Promise<RunningContext> => {
  try {
    const userContext = await zoomSdk.getUserContext();
    if (userContext.role === "attendee" || userContext.role === "panelist") {
      Tracker.setRunningContext("inMeeting");
      return "inMeeting";
    }
  } catch (e: any) {
    // Don't log sentry error when we not in a meeting
    // and instead just set the running context to inMainClient
    if (
      e.message &&
      e.message.includes(
        "only be called when the Zoom app is running in a meeting"
      )
    ) {
      Tracker.setRunningContext("inMainClient");
      return "inMainClient";
    } else {
      Sentry.captureMessage(
        `Error getting zoom user context: ${e.message}`,
        "error"
      );
      throw e;
    }
  }

  return "unknown";
};

export const getRunningContext = async () => {
  logger.debug("getRunningContext configureZoomSdk");
  await configureZoomSdk();
  try {
    const runningContext = (await zoomSdk.getRunningContext()).context;
    Tracker.setRunningContext(runningContext);
    return runningContext;
  } catch (e: any) {
    Sentry.captureMessage(
      `Error getting zoom running context, attempting inferred context: ${e.message}`,
      "error"
    );
  }

  try {
    const runningContext = await getInferredRunningContext();
    Tracker.setRunningContext(runningContext);
    return runningContext;
  } catch (e: any) {
    Sentry.captureMessage(
      `Error getting zoom running context: ${e.message}`,
      "error"
    );
  }
  return "unknown";
};

const QUERY_KEY = "zoomRunningContext";

const useZoomRunningContextQuery = (): UseZoomRunningContextResponse => {
  const { client } = useApps();

  const queryClient = useQueryClient();
  const query = useQuery<RunningContext>({
    queryKey: [QUERY_KEY],
    queryFn: async () => (await getRunningContext()) ?? "unknown",
    enabled: client === "zoom",
  });

  const handleRunningContextChange = useCallback(async () => {
    logger.debug("handleRunningContextChange, refetching");
    return queryClient.refetchQueries({ queryKey: [QUERY_KEY] });
  }, []);

  useEffect(() => {
    if (client !== "zoom") return;

    logger.debug("useZoomRunninContextQuery configureZoomSdk");
    configureZoomSdk().then(() => {
      zoomSdk.onRunningContextChange(handleRunningContextChange);
    });

    return () => {
      zoomSdk.removeEventListener(
        "onRunningContextChange",
        handleRunningContextChange
      );
    };
  }, [handleRunningContextChange, client]);

  return { ...query, runningContext: query.data ?? ("waiting" as const) };
};

export default useZoomRunningContextQuery;
